import { useEffect, useState } from "react";
import { BUSINESS, SERVICES, PROMOTIONS } from "../data/siteData";

// Bump this when default data structure changes to invalidate old localStorage.
const DATA_VERSION = "v3-pen-aura-2026-02-14";

const KEYS = {
  version: "aura_version",
  business: "aura_business",
  services: "aura_services",
  promotions: "aura_promotions",
};

// One-shot invalidation: if the stored version differs from current, wipe overrides.
if (typeof window !== "undefined") {
  try {
    const stored = localStorage.getItem(KEYS.version);
    if (stored !== DATA_VERSION) {
      localStorage.removeItem(KEYS.business);
      localStorage.removeItem(KEYS.services);
      localStorage.removeItem(KEYS.promotions);
      localStorage.setItem(KEYS.version, DATA_VERSION);
    }
  } catch (_) { /* ignore */ }
}

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function save(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent("aura-store-update", { detail: { key } }));
}

export function useStore() {
  const [business, setBusiness] = useState(() => load(KEYS.business, BUSINESS));
  const [services, setServices] = useState(() => load(KEYS.services, SERVICES));
  const [promotions, setPromotions] = useState(() => load(KEYS.promotions, PROMOTIONS));

  useEffect(() => {
    const handler = () => {
      setBusiness(load(KEYS.business, BUSINESS));
      setServices(load(KEYS.services, SERVICES));
      setPromotions(load(KEYS.promotions, PROMOTIONS));
    };
    window.addEventListener("aura-store-update", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("aura-store-update", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return {
    business,
    services,
    promotions,
    updateBusiness: (next) => save(KEYS.business, next),
    updateServices: (next) => save(KEYS.services, next),
    updatePromotions: (next) => save(KEYS.promotions, next),
    resetAll: () => {
      localStorage.removeItem(KEYS.business);
      localStorage.removeItem(KEYS.services);
      localStorage.removeItem(KEYS.promotions);
      window.dispatchEvent(new CustomEvent("aura-store-update", { detail: { key: "all" } }));
    },
  };
}

export function formatCOP(n) {
  if (n === null || n === undefined || isNaN(n)) return "";
  return new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);
}

export function buildWhatsappLink(phone, message) {
  const cleaned = (phone || "").replace(/\D/g, "");
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
}
